/**
 * AnalysisProgressCard.jsx — Iter 92 Phase 6 redesign.
 *
 * Single calm progress bar that maps the 11 technical backend stages
 * onto 3 user-facing macro-stages (Caricamento / Analisi /
 * Finalizzazione) on a continuous 0 → 100% bar. Detailed stage
 * messages, item counts, ETA — all removed: only the macro-label and
 * the percentage stay visible.
 *
 * The macro-mapping lives in `useAnalysisJob.mapToMacroProgress`.
 */
import { Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { mapToMacroProgress } from "@/hooks/useAnalysisJob";
import { useEffect, useRef, useState } from "react";


function fmtTime(sec) {
  if (sec == null || !Number.isFinite(sec)) return "--";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}


export function AnalysisProgressCard({
  job,
  elapsedSec,
  onCancel,
  onRetry,
}) {
  // Monotonic percent ratchet — the progress bar must never go backwards
  // even if the backend tick order produces a momentary regression.
  // Iter 92 Phase 6 fix-up — visual interpolation between ticks so a
  // sub-3s fast-path analysis doesn't "teleport" 0→75; the displayed
  // percent eases up to the target at ~30% per RAF frame.
  const maxPctRef = useRef(0);
  const targetPctRef = useRef(0);
  const rafRef = useRef(null);
  const [displayPct, setDisplayPct] = useState(0);
  const [displayLabel, setDisplayLabel] = useState("Caricamento");

  // Animation loop: ease displayPct toward targetPctRef.
  useEffect(() => {
    const tick = () => {
      setDisplayPct((cur) => {
        const target = targetPctRef.current;
        if (cur >= target) return target;
        const step = Math.max(0.5, (target - cur) * 0.18);
        return Math.min(target, cur + step);
      });
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!job) {
      maxPctRef.current = 0;
      targetPctRef.current = 0;
      setDisplayPct(0);
      setDisplayLabel("Caricamento");
      return;
    }
    const { macroLabel, percent } = mapToMacroProgress(job.progress);
    if (percent >= maxPctRef.current) {
      maxPctRef.current = percent;
      targetPctRef.current = percent;
    }
    setDisplayLabel(macroLabel);
    if (job.status === "done") {
      maxPctRef.current = 100;
      targetPctRef.current = 100;
    }
  }, [job]);

  if (!job) return null;

  const isCancelled = job.status === "cancelled";
  const isFailed = job.status === "failed";
  const isFinalizing = job.status === "finalizing";

  return (
    <div
      data-testid="analysis-progress-card"
      className="rounded-xl border border-brand/40 bg-brand/5 px-5 py-4 space-y-3"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {!isCancelled && !isFailed && (
            <Loader2 className="h-5 w-5 text-brand-accent animate-spin shrink-0" />
          )}
          <div
            data-testid="analysis-progress-stage"
            className="text-sm font-semibold text-brand-accent truncate"
          >
            {isFailed ? "Errore" : isCancelled ? "Annullato" : displayLabel}
          </div>
        </div>
        {!isCancelled && !isFailed && !isFinalizing && (
          <Button
            data-testid="analysis-cancel-btn"
            onClick={onCancel}
            variant="ghost"
            size="sm"
            className="text-brand-accent hover:text-content hover:bg-brand/20 shrink-0"
            disabled={job.cancel_requested}
          >
            <X className="h-4 w-4 mr-1" />
            {job.cancel_requested ? "Annullamento…" : "Annulla"}
          </Button>
        )}
        {isFailed && onRetry && (
          <Button
            data-testid="analysis-retry-btn"
            onClick={onRetry}
            size="sm"
            className="bg-brand hover:bg-brand-hover text-content-on-brand shrink-0"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Riprova
          </Button>
        )}
      </div>

      {/* UX-005 — surface the failure reason inline: the selection is
          preserved, one click re-runs with identical inputs. */}
      {isFailed && (
        <div
          data-testid="analysis-failed-detail"
          className="text-xs text-danger"
        >
          {job.error || "Errore sconosciuto."} La selezione è stata
          conservata: premi «Riprova» per rieseguire con gli stessi input.
        </div>
      )}

      <div className="space-y-1">
        <Progress
          value={Math.round(displayPct)}
          data-testid="analysis-progress-bar"
          className="h-2 bg-brand-subtle/50"
        />
        <div className="flex items-center justify-between text-label text-brand-accent/70 tabular-nums">
          <span data-testid="analysis-progress-percent">{Math.round(displayPct)}%</span>
          {elapsedSec > 0 && <span>Trascorso: {fmtTime(elapsedSec)}</span>}
        </div>
      </div>
    </div>
  );
}
